import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatTimestamp,
  parseTimestamp,
  segmentText,
  type TranscriptWord,
} from '@captionflow/caption-engine';
import { TEMPLATES_BY_ID } from '@captionflow/template-schema';
import { useEditor } from '../state/editorStore';
import { playbackClock } from '../playback/clock';

/**
 * YouTube-style timestamped transcript. Each line shows editable start + end
 * times and the sentence text. Editing text re-splits into timed words; editing
 * a time remaps the line's words into the new window. Manual mode (default)
 * allows overlaps but flags them; Auto mode clamps to neighbours.
 */
export function SentenceView() {
  const segments = useEditor((s) => s.segments);
  const words = useEditor((s) => s.words);
  const timingMode = useEditor((s) => s.timingMode);
  const setTimingMode = useEditor((s) => s.setTimingMode);
  const editSegmentText = useEditor((s) => s.editSegmentText);
  const insertSegmentAfter = useEditor((s) => s.insertSegmentAfter);
  const setSegmentTiming = useEditor((s) => s.setSegmentTiming);
  const deleteSegment = useEditor((s) => s.deleteSegment);
  const hoverSegmentId = useEditor((s) => s.hoverSegmentId);
  const setHoverSegment = useEditor((s) => s.setHoverSegment);
  const selectedSegmentIds = useEditor((s) => s.selectedSegmentIds);
  const toggleSegmentSelected = useEditor((s) => s.toggleSegmentSelected);
  const setSelectedSegments = useEditor((s) => s.setSelectedSegments);
  const clearSegmentSelection = useEditor((s) => s.clearSegmentSelection);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const anchorRef = useRef<number | null>(null);

  const byId = useMemo(() => new Map(words.map((w) => [w.id, w])), [words]);
  const orderedIds = useMemo(() => segments.map((s) => s.id), [segments]);
  const selectedSet = useMemo(() => new Set(selectedSegmentIds), [selectedSegmentIds]);
  const allSelected = segments.length > 0 && selectedSegmentIds.length === segments.length;
  const someSelected = selectedSegmentIds.length > 0 && !allSelected;

  // Click = select only this line (click again to deselect). Cmd/Ctrl = toggle
  // add. Shift = contiguous range from the last-clicked anchor. Standard list UX.
  const selectLine = (index: number, mods: { shift: boolean; meta: boolean }) => {
    const id = orderedIds[index];
    if (!id) return;
    if (mods.shift && anchorRef.current != null) {
      const a = Math.min(anchorRef.current, index);
      const b = Math.max(anchorRef.current, index);
      const range = orderedIds.slice(a, b + 1);
      setSelectedSegments(Array.from(new Set([...selectedSegmentIds, ...range])));
    } else if (mods.meta) {
      anchorRef.current = index;
      toggleSegmentSelected(id, true);
    } else {
      anchorRef.current = index;
      setSelectedSegments(selectedSet.has(id) && selectedSegmentIds.length === 1 ? [] : [id]);
    }
  };
  const toggleAll = () => (allSelected ? clearSegmentSelection() : setSelectedSegments(orderedIds));

  // Overlap detection (segments are kept in time order by the store).
  const overlapIds = useMemo(() => {
    const bad = new Set<string>();
    for (let i = 1; i < segments.length; i++) {
      if (segments[i]!.startMs < segments[i - 1]!.endMs) {
        bad.add(segments[i]!.id);
        bad.add(segments[i - 1]!.id);
      }
    }
    return bad;
  }, [segments]);

  useEffect(() => {
    let last = 0;
    return playbackClock.subscribe((t) => {
      const now = performance.now();
      if (now - last < 120) return;
      last = now;
      const seg = segments.find((s) => t >= s.startMs && t < s.endMs);
      setActiveId(seg?.id ?? null);
    });
  }, [segments]);

  return (
    <div className="sentence-view">
      <div className="sv-toolbar">
        <label className="select-all" title="Select all caption lines">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={toggleAll}
          />
          All
        </label>
        {selectedSegmentIds.length > 0 ? (
          <div className="sel-info">
            <strong>{selectedSegmentIds.length}</strong> selected
            <button className="link-btn" onClick={clearSegmentSelection}>
              Clear
            </button>
            <span className="sel-hint">- pick a style on the left</span>
          </div>
        ) : (
          <span className="sel-hint">Tick lines to style them · shift-click for a range</span>
        )}
        <div className="mode-toggle" title="Timing mode">
          <button
            className={timingMode === 'manual' ? 'on' : ''}
            onClick={() => setTimingMode('manual')}
          >
            Manual
          </button>
          <button className={timingMode === 'auto' ? 'on' : ''} onClick={() => setTimingMode('auto')}>
            Auto
          </button>
        </div>
      </div>
      {overlapIds.size > 0 && (
        <div className="overlap-warn">
          ⚠ {overlapIds.size} line(s) overlap. Fix the highlighted times, or switch to Auto to clamp
          automatically.
        </div>
      )}
      {segments.map((seg, index) => {
        const memberWords = seg.wordIds
          .map((id) => byId.get(id))
          .filter((w): w is TranscriptWord => Boolean(w));
        return (
          <SentenceRow
            key={seg.id}
            onSelect={(mods) => selectLine(index, mods)}
            startMs={seg.startMs}
            endMs={seg.endMs}
            text={segmentText(memberWords)}
            words={memberWords}
            active={seg.id === activeId}
            overlap={overlapIds.has(seg.id)}
            hover={seg.id === hoverSegmentId}
            autoFocus={seg.id === focusId}
            selected={selectedSegmentIds.includes(seg.id)}
            styleName={seg.templateId ? (TEMPLATES_BY_ID[seg.templateId]?.name ?? null) : null}
            onHoverChange={(h) => setHoverSegment(h ? seg.id : null)}
            onSeek={() => playbackClock.seek(seg.startMs)}
            onCommit={(t) => editSegmentText(seg.id, t)}
            onSetStart={(ms) => setSegmentTiming(seg.id, { startMs: ms })}
            onSetEnd={(ms) => setSegmentTiming(seg.id, { endMs: ms })}
            onDelete={() => deleteSegment(seg.id)}
            onInsertAfter={() => {
              const id = insertSegmentAfter(seg.id);
              if (id) setFocusId(id);
            }}
          />
        );
      })}
      {segments.length === 0 && <p className="muted">No captions yet.</p>}
    </div>
  );
}

/** A time field that edits its own draft and commits on Enter/blur. */
function TimeField({
  ms,
  onCommit,
  label,
}: {
  ms: number;
  onCommit: (ms: number) => void;
  label: string;
}) {
  const [draft, setDraft] = useState(formatTimestamp(ms));
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(formatTimestamp(ms));
  }, [ms, editing]);

  const commit = () => {
    setEditing(false);
    const parsed = parseTimestamp(draft);
    if (parsed === null) {
      setError(true);
      setDraft(formatTimestamp(ms));
      setTimeout(() => setError(false), 1200);
      return;
    }
    if (parsed !== ms) onCommit(parsed);
  };

  return (
    <input
      className={error ? 'ts-input error' : 'ts-input'}
      value={draft}
      aria-label={label}
      title={`${label} (e.g. 0:06)`}
      onFocus={() => setEditing(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
          (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
          setDraft(formatTimestamp(ms));
          setEditing(false);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function SentenceRow({
  startMs,
  endMs,
  text,
  words,
  active,
  overlap,
  hover,
  autoFocus,
  selected,
  styleName,
  onSelect,
  onHoverChange,
  onSeek,
  onCommit,
  onSetStart,
  onSetEnd,
  onDelete,
  onInsertAfter,
}: {
  startMs: number;
  endMs: number;
  text: string;
  words: TranscriptWord[];
  active: boolean;
  overlap: boolean;
  hover: boolean;
  autoFocus: boolean;
  selected: boolean;
  styleName: string | null;
  onSelect: (mods: { shift: boolean; meta: boolean }) => void;
  onHoverChange: (hovered: boolean) => void;
  onSeek: () => void;
  onCommit: (text: string) => void;
  onSetStart: (ms: number) => void;
  onSetEnd: (ms: number) => void;
  onDelete: () => void;
  onInsertAfter: () => void;
}) {
  const [draft, setDraft] = useState(text);
  const [editing, setEditing] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) setDraft(text);
  }, [text, editing]);

  useEffect(() => {
    if (autoFocus) setEditing(true);
  }, [autoFocus]);

  // Focus the textarea whenever we enter edit mode (click or autoFocus).
  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.select();
    }
  }, [editing]);

  // When highlighted from the timeline, scroll the row into view.
  useEffect(() => {
    if (hover) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [hover]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() !== text.trim()) onCommit(draft);
  };

  const cls = [
    'sentence-row',
    active && 'active',
    overlap && 'overlap',
    hover && 'hover',
    selected && 'sel',
  ]
    .filter(Boolean)
    .join(' ');

  // Clicking the row body (not the text/time inputs or buttons) selects the
  // line - an easy, large target. Interactive children stop propagation.
  const onRowClick = (e: React.MouseEvent) => {
    const el = e.target as HTMLElement;
    if (el.closest('textarea, input, button, .sentence-read')) return;
    onSelect({ shift: e.shiftKey, meta: e.metaKey || e.ctrlKey });
  };

  return (
    <div
      className={cls}
      ref={rowRef}
      onClick={onRowClick}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <input
        type="checkbox"
        className="line-select"
        checked={selected}
        title="Select this line (shift-click for a range)"
        onClick={(e) => {
          e.stopPropagation();
          onSelect({ shift: e.shiftKey, meta: e.metaKey || e.ctrlKey });
        }}
        onChange={() => {}}
      />
      <button
        className="ts-jump"
        onClick={(e) => {
          e.stopPropagation();
          onSeek();
        }}
        title="Jump to this time"
        aria-label="Jump"
      >
        ▶
      </button>
      {styleName && (
        <span className="line-style-badge" title={`This line uses the ${styleName} style`}>
          {styleName}
        </span>
      )}
      <div className="ts-range">
        <TimeField ms={startMs} onCommit={onSetStart} label="Start time" />
        <span className="ts-dash">–</span>
        <TimeField ms={endMs} onCommit={onSetEnd} label="End time" />
      </div>
      {editing ? (
        <textarea
          ref={taRef}
          className="sentence-text"
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit();
              taRef.current?.blur();
            } else if (e.key === 'Escape') {
              setDraft(text);
              setEditing(false);
              taRef.current?.blur();
            }
          }}
        />
      ) : (
        <div
          className="sentence-text sentence-read"
          role="textbox"
          tabIndex={0}
          title="Click to edit - the bold word is the AI-picked key word"
          onClick={() => setEditing(true)}
          onFocus={() => setEditing(true)}
        >
          {words.length === 0
            ? text
            : words.map((w, i) => (
                <span key={w.id}>
                  {i > 0 ? ' ' : ''}
                  {w.emphasized ? <strong className="key-word">{w.text}</strong> : w.text}
                </span>
              ))}
        </div>
      )}
      <div className="row-actions">
        <button className="add-line" onClick={onInsertAfter} title="Insert a caption line below">
          +
        </button>
        <button
          className="del-line"
          onClick={onDelete}
          title="Delete this line"
          aria-label="Delete line"
        >
          ×
        </button>
      </div>
    </div>
  );
}
